import { useFetcher } from '@remix-run/react'
import { captureException } from '@sentry/react'
import { useMachine } from '@xstate/react'
import React, {
	createContext,
	useContext,
	useEffect,
	type ReactNode,
	useState,
	useCallback,
	useRef,
	useMemo,
} from 'react'
import {
	assign,
	createMachine,
	fromCallback,
	fromPromise,
	type ErrorActorEvent,
} from 'xstate'
import { usePush } from '#app/hooks/use-push.ts'
import { NotificationType } from '#app/models/push-subscription._._utils.ts'
import {
	fetcherError,
	fetcherSuccess,
	type FetcherData,
	fetcherErrorMessages,
} from '#app/utils/fetcher-helpers.tsx'
import { createLogger } from '#app/utils/logger.ts'
import { useNavigatorProperty } from '#app/utils/navigator-permissions.ts'
import { parseUserAgent } from '#app/utils/user-agent.ts'

const pushLogger = createLogger('PushProvider', false, 'debug')

const PUSH_VERIFICATION_INTERVAL = 30000 // 30 seconds
const VISIBILITY_VERIFICATION_DELAY = 300000 // 5 minutes
const MAX_REGISTRATION_ATTEMPTS = 5 // Max attempts before blocking
const REGISTRATION_WINDOW_MS = 60 * 1000 // 1 minute window

// --- XState Machine Definition ---

interface PushMachineContext {
	pushSubscription: PushSubscription | null
	registrationAttempts: number
	firstAttemptTimestamp: number
	lastVisibilityVerification: number
	errorMessage: string | null
	vapidPublicKey: string // Passed in via input
	userId: string | null // Passed in via input
	triggerTestPushFetch: boolean // Flag to signal effect
}

type PushMachineEvent =
	// External Events / Inputs
	| { type: 'PROVIDER_INIT'; payload: ProviderInitPayload }
	| { type: 'USER_LOGGED_OUT' }
	| { type: 'PERMISSION_REQUESTED' } // User clicked 'allow' button
	| { type: 'VISIBILITY_CHECK' }
	| { type: 'START_SW_UPDATE' }
	| { type: 'END_SW_UPDATE' }
	| { type: 'TEST_PUSH_REQUESTED' } // Event to trigger test push
	// Internal Events (from invoked services, intervals, etc.)
	| { type: 'PERMISSION_GRANTED' } // Browser permission API result
	| { type: 'PERMISSION_DENIED' } // Browser permission API result
	| { type: 'BROWSER_SUBSCRIBE_SUCCESS'; output: PushSubscription } // Output from service
	| {
			type: 'BROWSER_SUBSCRIBE_ERROR'
			error: { message: string; isRateLimit?: boolean }
	  } // Error from service
	| { type: 'BROWSER_UNSUBSCRIBE_SUCCESS' }
	| { type: 'BROWSER_UNSUBSCRIBE_ERROR'; error: string } // Error from service
	| { type: 'SERVER_REGISTER_SUCCESS' }
	| { type: 'SERVER_REGISTER_ERROR'; error: string } // Error from service
	| { type: 'SERVER_VERIFY_SUCCESS' }
	| { type: 'SERVER_VERIFY_ERROR'; error: string } // Error from service
	| { type: 'VERIFICATION_INTERVAL_TICK' }
	| { type: 'CLEAR_TEST_PUSH_TRIGGER' } // Event to clear the trigger flag

// Payload for initial provider data
interface ProviderInitPayload {
	isServiceWorkerAvailable: boolean
	canSendPush: boolean
	initialPushSubscription: PushSubscription | null
	initialCheckComplete: boolean
	userId: string | null
	vapidPublicKey: string
}

// Define the type for the machine
export const pushMachine = createMachine(
	{
		types: {
			context: {} as PushMachineContext,
			events: {} as PushMachineEvent,
			input: {} as Pick<PushMachineContext, 'userId' | 'vapidPublicKey'>,
		},
		id: 'pushManager',
		// Initial context setup using provided input (userId, vapidPublicKey)
		context: ({ input }) => ({
			pushSubscription: null,
			registrationAttempts: 0,
			firstAttemptTimestamp: 0,
			lastVisibilityVerification: Date.now(),
			errorMessage: null,
			userId: input.userId,
			vapidPublicKey: input.vapidPublicKey,
			triggerTestPushFetch: false,
		}),
		// Start state
		initial: 'initializing',
		// State definitions
		states: {
			// Initial state, determines the next state based on provider initialization data
			initializing: {
				// Reset context on entering initializing state, ensuring clean slate after logout
				entry: assign({
					pushSubscription: null,
					registrationAttempts: 0,
					firstAttemptTimestamp: 0,
					errorMessage: null,
					triggerTestPushFetch: false,
					// Keep userId and vapidPublicKey from input/previous state if available
					userId: ({ context }) => context.userId,
					vapidPublicKey: ({ context }) => context.vapidPublicKey,
				}),
				on: {
					PROVIDER_INIT: [
						{
							target: 'idle',
							guard: 'shouldBeIdle',
							actions: assign({
								userId: ({ event }) => event.payload.userId,
								vapidPublicKey: ({ event }) => event.payload.vapidPublicKey,
								pushSubscription: null, // Explicitly null here too
							}),
						},
						{
							target: 'verifying_initial',
							guard: 'hasInitialSubscription',
							actions: assign({
								pushSubscription: ({ event }) =>
									event.payload.initialPushSubscription,
								userId: ({ event }) => event.payload.userId,
								vapidPublicKey: ({ event }) => event.payload.vapidPublicKey,
							}),
						},
						{
							target: 'needs_permission',
							guard: 'needsPermission',
							actions: assign({
								userId: ({ event }) => event.payload.userId,
								vapidPublicKey: ({ event }) => event.payload.vapidPublicKey,
								pushSubscription: null,
							}),
						},
						{
							target: 'subscribing_browser',
							actions: assign({
								userId: ({ event }) => event.payload.userId,
								vapidPublicKey: ({ event }) => event.payload.vapidPublicKey,
								pushSubscription: null,
							}),
						},
					],
					// If logged out while initializing, stay initializing (or go idle if that makes more sense)
					// Let's stay initializing to allow PROVIDER_INIT to potentially move to idle later
					USER_LOGGED_OUT: { target: 'initializing' },
				},
			},
			// State where push notifications are not active (user logged out, SW unavailable, etc.)
			idle: {
				// Reset context on entering idle state
				entry: assign({
					pushSubscription: null,
					registrationAttempts: 0,
					firstAttemptTimestamp: 0,
					errorMessage: null,
					triggerTestPushFetch: false, // Reset test push flag too
				}),
				// Re-initialize if provider data changes (e.g., user logs in)
				on: { PROVIDER_INIT: { target: 'initializing' } },
			},
			// Waiting for the user to grant browser notification permission
			needs_permission: {
				entry: assign({ errorMessage: null }),
				on: {
					PERMISSION_GRANTED: { target: 'subscribing_browser' },
					PERMISSION_DENIED: {
						target: 'error',
						actions: assign({ errorMessage: 'Permission Denied' }),
					},
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
				},
			},
			// Attempting to subscribe the browser using the Push API
			subscribing_browser: {
				entry: assign({ errorMessage: null }),
				invoke: {
					id: 'browserSubscribe',
					src: 'subscribeBrowserService',
					onDone: {
						target: 'registering_server',
						actions: assign({
							pushSubscription: ({ event }) => event.output,
							registrationAttempts: 0,
							firstAttemptTimestamp: 0,
						}),
					},
					onError: [
						{
							target: 'rate_limited',
							guard: ({ event }: { event: ErrorActorEvent }) =>
								(event.error as any)?.isRateLimit,
							actions: assign({ errorMessage: 'Rate limit exceeded' }),
						},
						{
							target: 'error',
							actions: assign({
								errorMessage: ({ event }: { event: ErrorActorEvent }) =>
									`Browser subscribe failed: ${(event.error as any)?.message ?? 'Unknown error'}`,
							}),
						},
					],
				},
				on: {
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
				},
			},
			// State entered when browser subscription fails due to rate limiting
			rate_limited: {
				entry: assign({ errorMessage: 'Rate limit exceeded' }),
				after: {
					REGISTRATION_RETRY_DELAY: { target: 'subscribing_browser' },
				},
				on: {
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
				},
			},
			// Registering the browser's PushSubscription with the application server
			registering_server: {
				entry: assign({ errorMessage: null }),
				on: {
					SERVER_REGISTER_SUCCESS: { target: 'subscribed_idle' },
					SERVER_REGISTER_ERROR: {
						target: 'error',
						actions: assign({
							errorMessage: ({ event }) =>
								`Server register failed: ${event.error}`,
						}),
					},
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
				},
			},
			// Verifying the newly registered subscription with the server (initial verification)
			verifying_initial: {
				entry: assign({ errorMessage: null }),
				on: {
					SERVER_VERIFY_SUCCESS: { target: 'subscribed_idle' },
					SERVER_VERIFY_ERROR: {
						target: 'unsubscribing',
						actions: assign({
							errorMessage: ({ event }) =>
								`Initial verification failed: ${event.error}`,
						}),
					},
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
				},
			},
			// Stable state: Successfully subscribed and verified
			subscribed_idle: {
				entry: assign({ errorMessage: null }),
				invoke: {
					id: 'intervalTimer',
					src: 'verificationIntervalService',
				},
				on: {
					VERIFICATION_INTERVAL_TICK: { target: 'subscribed_verifying' },
					VISIBILITY_CHECK: {
						target: 'subscribed_verifying',
						guard: 'isVisibilityCheckAllowed',
						actions: 'updateLastVisibilityTime',
					},
					TEST_PUSH_REQUESTED: { actions: 'sendTestPush' },
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
					START_SW_UPDATE: { target: 'sw_updating' },
					CLEAR_TEST_PUSH_TRIGGER: { actions: 'clearTestPushTrigger' },
				},
			},
			// Periodically verifying the subscription with the server while subscribed
			subscribed_verifying: {
				entry: assign({ errorMessage: null }),
				on: {
					SERVER_VERIFY_SUCCESS: { target: 'subscribed_idle' },
					SERVER_VERIFY_ERROR: {
						target: 'unsubscribing',
						actions: assign({
							errorMessage: ({ event }) =>
								`Periodic verification failed: ${event.error}`,
						}),
					},
					PROVIDER_INIT: { target: 'initializing' },
					USER_LOGGED_OUT: { target: 'initializing' }, // Go back to start on logout
					START_SW_UPDATE: { target: 'sw_updating' },
				},
			},
			// Attempting to unsubscribe the browser (e.g., after verification failure)
			unsubscribing: {
				invoke: {
					id: 'browserUnsubscribe',
					src: 'unsubscribeBrowserService',
					onDone: {
						target: 'subscribing_browser', // Try to re-subscribe after browser cleanup
						actions: assign({ pushSubscription: null }),
					},
					onError: {
						target: 'error',
						actions: assign({
							errorMessage: ({ event }: { event: ErrorActorEvent }) =>
								`Browser unsubscribe failed: ${String(event.error)}`,
						}),
					},
				},
				on: {
					PROVIDER_INIT: { target: 'initializing' },
					// If logout happens during browser unsubscribe, just go back to start
					USER_LOGGED_OUT: { target: 'initializing' },
				},
			},
			// Removed unregistering_server state
			// State indicating the service worker is being updated
			sw_updating: {
				on: {
					END_SW_UPDATE: { target: 'initializing' }, // Re-initialize after SW update
					// If logout happens during SW update, go back to start
					USER_LOGGED_OUT: { target: 'initializing' },
				},
			},
			// General error state
			error: {
				entry: 'logError',
				on: {
					PROVIDER_INIT: { target: 'initializing' },
					// If logout happens in error state, go back to start
					USER_LOGGED_OUT: { target: 'initializing' },
				},
			},
		},
	},
	// --- Machine Options (Actions, Actors, Guards, Delays) ---
	{
		actions: {
			updateLastVisibilityTime: assign({
				lastVisibilityVerification: () => Date.now(),
			}),
			logError: ({ context, event }) => {
				const message = context.errorMessage || 'Unknown push machine error'
				pushLogger.error('Push Machine Error', { message, event })
				captureException(`Push Machine Error: ${message}`, {
					extra: { context, event },
					tags: { push_error_type: 'xstate_machine_error' },
				})
			},
			sendTestPush: assign({ triggerTestPushFetch: true }),
			clearTestPushTrigger: assign({ triggerTestPushFetch: false }),
		},
		actors: {
			/* Placeholders for services implemented in PushProvider */
			// subscribeBrowserService: fromPromise(async () => { ... })
			// unsubscribeBrowserService: fromPromise(async () => { ... })
			// verificationIntervalService: fromCallback(({ sendBack }) => { ... })
		},
		guards: {
			shouldBeIdle: ({ event }) => {
				if (event.type !== 'PROVIDER_INIT') return false
				return (
					!event.payload.userId ||
					!event.payload.isServiceWorkerAvailable ||
					!event.payload.initialCheckComplete
				)
			},
			hasInitialSubscription: ({ event }) => {
				if (event.type !== 'PROVIDER_INIT') return false
				return !!event.payload.initialPushSubscription
			},
			needsPermission: ({ event }) => {
				if (event.type !== 'PROVIDER_INIT') return false
				return !event.payload.canSendPush
			},
			isVisibilityCheckAllowed: ({ context }) => {
				const now = Date.now()
				return (
					now - context.lastVisibilityVerification >=
					VISIBILITY_VERIFICATION_DELAY
				)
			},
			// Removed hasSubscriptionInContext guard as it's no longer needed
		},
		delays: {
			REGISTRATION_RETRY_DELAY: REGISTRATION_WINDOW_MS,
		},
	},
)

// --- Context Definition (Interface Unchanged) ---
interface PushNotificationContextType {
	isSubscribed: boolean
	canSendPush: boolean
	pushSubscription: PushSubscription | null
	notificationPermission: NotificationPermission
	requestPermission: () => Promise<NotificationPermission>
	testPushNotifications: () => void
	isLoading: boolean
	testSent: boolean
	initialCheckComplete: boolean
	isUpdatingSW: boolean
	setIsUpdatingSW: (isUpdating: boolean) => void
	stateValue: ReturnType<typeof useMachine<typeof pushMachine>>[0]['value'] // Expose raw state value
	unsubscribeFromPush: (
		callback?: () => void,
		errorCallback?: (error: unknown) => void,
	) => void
}

const PushNotificationContext =
	createContext<PushNotificationContextType | null>(null)

// --- Provider Component ---
interface PusherProviderProps {
	userId: string | null
	vapidPublicKey: string
	children: ReactNode
}

export function PushProvider({
	vapidPublicKey,
	userId,
	children,
}: PusherProviderProps) {
	const { isAvailable: isServiceWorkerAvailable } =
		useNavigatorProperty('serviceWorker')

	const [testSent, setTestSent] = useState(false)

	// --- Hooks ---
	const {
		requestPermission: browserRequestPermission,
		canSendPush,
		initialCheckComplete,
		pushSubscription: browserPushSubscription,
		subscribeToPush,
		unsubscribeFromPush,
		notificationPermission,
	} = usePush()

	// Fetchers for server communication
	const verifyFetcher = useFetcher<FetcherData>()
	const registerFetcher = useFetcher<FetcherData>()
	// Removed unregisterFetcher
	const testNotificationFetcher = useFetcher<FetcherData>()

	// --- Rate Limiting Refs/Helpers ---
	const registrationAttemptsRef = useRef(0)
	const firstAttemptTimestampRef = useRef(0)
	const processedRegisterDataRef = useRef<FetcherData | null>(null)
	const processedVerifyDataRef = useRef<FetcherData | null>(null)
	// Removed processedUnregisterDataRef
	const isInitializedRef = useRef(false)
	const verificationFetchInFlightRef = useRef(false)
	const registrationFetchInFlightRef = useRef(false)

	const checkRateLimit = useCallback(() => {
		const now = Date.now()
		if (now - firstAttemptTimestampRef.current > REGISTRATION_WINDOW_MS) {
			pushLogger.info('Rate limit check: Window reset.', {
				now,
				firstAttemptTimestamp: firstAttemptTimestampRef.current,
			})
			registrationAttemptsRef.current = 0
			firstAttemptTimestampRef.current = 0
		}
		const limited = registrationAttemptsRef.current >= MAX_REGISTRATION_ATTEMPTS
		if (limited) {
			pushLogger.error('Rate limit check: Limit reached', {
				attempts: registrationAttemptsRef.current,
			})
		}
		return { limited }
	}, [])

	const incrementAttempt = useCallback(() => {
		if (registrationAttemptsRef.current === 0) {
			firstAttemptTimestampRef.current = Date.now()
		}
		registrationAttemptsRef.current++
		pushLogger.info('Rate limit check: Incremented attempt', {
			attempt: registrationAttemptsRef.current,
		})
	}, [])

	const resetRateLimit = useCallback(() => {
		registrationAttemptsRef.current = 0
		firstAttemptTimestampRef.current = 0
		pushLogger.info('Rate limit check: Reset.', {
			registrationAttempts: registrationAttemptsRef.current,
			firstAttemptTimestamp: firstAttemptTimestampRef.current,
		})
	}, [])

	// --- XState Machine Instance ---
	const pushMachineInstance = React.useMemo(() => {
		return pushMachine.provide({
			actors: {
				// Service for subscribing browser
				subscribeBrowserService: fromPromise(async () => {
					const { limited } = checkRateLimit()
					if (limited) {
						captureException('Push registration rate limit reached', {
							extra: {
								userId,
								attempts: registrationAttemptsRef.current,
								windowSeconds: REGISTRATION_WINDOW_MS / 1000,
							},
							tags: { push_error_type: 'rate_limit_exceeded' },
						})
						throw { message: 'Rate limit exceeded', isRateLimit: true }
					}
					incrementAttempt()
					return new Promise<PushSubscription>((resolve, reject) => {
						subscribeToPush(
							vapidPublicKey,
							(sub) => {
								resetRateLimit()
								resolve(sub)
							},
							(err) => reject({ message: String(err) }),
						)
					})
				}),
				// Service for unsubscribing browser (used on verification failure)
				unsubscribeBrowserService: fromPromise(() => {
					pushLogger.info('Service: Unsubscribing browser...')
					return new Promise<void>((resolve, reject) => {
						unsubscribeFromPush(
							() => {
								pushLogger.info('Browser unsubscribe successful.')
								resolve()
							},
							(err) => {
								pushLogger.error('Browser unsubscribe failed', {
									error: err,
								})
								reject(String(err)) // Reject with the error message
							},
						)
					})
				}),
				// Service for periodic verification timer
				verificationIntervalService: fromCallback(({ sendBack }) => {
					const intervalId = setInterval(() => {
						sendBack({ type: 'VERIFICATION_INTERVAL_TICK' })
					}, PUSH_VERIFICATION_INTERVAL)
					return () => clearInterval(intervalId)
				}),
			},
		})
	}, [
		userId,
		vapidPublicKey,
		subscribeToPush,
		unsubscribeFromPush,
		checkRateLimit,
		incrementAttempt,
		resetRateLimit,
	])

	const [state, send] = useMachine(pushMachineInstance, {
		input: { userId, vapidPublicKey },
	})

	// --- Fetcher Trigger Effects ---
	useEffect(() => {
		// Trigger Register Fetcher
		if (
			state.matches('registering_server') &&
			registerFetcher.state === 'idle' &&
			!registrationFetchInFlightRef.current
		) {
			pushLogger.info('REGISTERING SERVER')
			const subscription = state.context.pushSubscription
			if (!subscription) {
				pushLogger.error(
					'XState Effect: Cannot register, subscription missing in context',
				)
				return
			}

			pushLogger.info(
				'Effect[state=registering_server]: Setting fetch flag and triggering register fetcher...',
			)
			// Prevent processing stale data if this effect re-runs quickly
			processedRegisterDataRef.current = null
			registrationFetchInFlightRef.current = true
			const userAgentInfo = parseUserAgent(navigator.userAgent)
			registerFetcher.submit(
				{
					subscription: JSON.stringify(subscription),
					type: NotificationType.REGISTER_DEVICE,
					userAgentInfo: JSON.stringify(userAgentInfo),
				},
				{ method: 'POST', action: '/resources/push/register' },
			)
		}

		// Trigger Verify Fetcher
		if (
			(state.matches('verifying_initial') ||
				state.matches('subscribed_verifying')) &&
			verifyFetcher.state === 'idle' &&
			!verificationFetchInFlightRef.current
		) {
			pushLogger.info('VERIFYING SERVER')
			const subscription = state.context.pushSubscription
			if (!subscription) {
				pushLogger.error(
					`Effect[state=${String(state.value)}]: Cannot verify, subscription missing in context`,
					{ state: state.value },
				)
				return
			}

			pushLogger.info(
				`Effect[state=${String(state.value)}]: Setting fetch flag and triggering verify fetcher...`,
				{ state: state.value },
			)
			verificationFetchInFlightRef.current = true
			processedVerifyDataRef.current = null
			verifyFetcher.submit(
				{
					subscription: JSON.stringify(subscription),
					type: NotificationType.VERIFY_REGISTRATION,
				},
				{ method: 'POST', action: '/resources/push/verify' },
			)
		}

		// Removed Unregister Fetcher Trigger logic

		// Trigger Test Push Fetcher
		if (
			state.context.triggerTestPushFetch &&
			testNotificationFetcher.state === 'idle'
		) {
			const subscription = state.context.pushSubscription
			if (subscription) {
				pushLogger.info(
					'Effect[context.triggerTestPushFetch]: Triggering test push fetcher...',
				)
				testNotificationFetcher.submit(
					{
						subscription: JSON.stringify(subscription),
						type: NotificationType.TEST_NOTIFICATION,
					},
					{ method: 'POST', action: '/resources/push/test-push' },
				)
				setTestSent(true)
				send({ type: 'CLEAR_TEST_PUSH_TRIGGER' })
			} else {
				pushLogger.warn(
					'XState Effect: Cannot send test push, no subscription in context.',
				)
				send({ type: 'CLEAR_TEST_PUSH_TRIGGER' })
			}
		}
	}, [
		state,
		send,
		registerFetcher.state,
		verifyFetcher.state,
		processedVerifyDataRef.current,
		// Removed unregisterFetcher state dependency
		testNotificationFetcher.state,
	])

	// --- Fetcher Result Effects ---
	useEffect(() => {
		// Process Register Fetcher Result only when it becomes idle
		if (
			registerFetcher.state === 'idle' &&
			registerFetcher.data &&
			registerFetcher.data !== processedRegisterDataRef.current
		) {
			// Only process if machine is in the expected state
			if (state.matches('registering_server')) {
				if (fetcherSuccess(registerFetcher)) {
					pushLogger.info('Effect: Sending SERVER_REGISTER_SUCCESS')
					send({ type: 'SERVER_REGISTER_SUCCESS' })
				} else if (fetcherError(registerFetcher)) {
					// Use fetcherErrorMessages to extract errors correctly
					const errorMessages = fetcherErrorMessages(registerFetcher)
					const errorMessage =
						errorMessages.length > 0
							? errorMessages.join(', ')
							: 'Unknown registration error'
					pushLogger.error('Register Fetcher Error Data', {
						data: registerFetcher.data,
					})
					pushLogger.info('Effect: Sending SERVER_REGISTER_ERROR', {
						errorMessage,
					})
					send({ type: 'SERVER_REGISTER_ERROR', error: errorMessage })
				}
			} else {
				// Log if the fetcher finishes but the machine has moved on
				if (registerFetcher.data) {
					// Avoid logging on initial load
					pushLogger.warn(
						'Effect: Register fetcher finished idle, but machine state is not registering_server',
						{ state: state.value },
					)
				}
			}
			// Mark data as processed (or null if no data)
			processedRegisterDataRef.current = registerFetcher.data ?? null
			registrationFetchInFlightRef.current = false
		}
	}, [registerFetcher.state, registerFetcher.data, state.value, send])

	useEffect(() => {
		// Process Verify Fetcher Result only when it becomes idle
		if (
			verifyFetcher.state === 'idle' &&
			verifyFetcher.data &&
			verifyFetcher.data !== processedVerifyDataRef.current
		) {
			// Only process if machine is in the expected state
			if (
				state.matches('verifying_initial') ||
				state.matches('subscribed_verifying')
			) {
				if (fetcherSuccess(verifyFetcher)) {
					pushLogger.info('Effect: Sending SERVER_VERIFY_SUCCESS')
					send({ type: 'SERVER_VERIFY_SUCCESS' })
				} else if (fetcherError(verifyFetcher)) {
					// Correctly extract error messages
					const errorMessages = fetcherErrorMessages(verifyFetcher)
					const errorMessage =
						errorMessages.length > 0
							? errorMessages.join(', ') // Join multiple errors if any
							: 'Unknown verification error' // Fallback
					pushLogger.info('Effect: Sending SERVER_VERIFY_ERROR', {
						errorMessage,
					})
					send({ type: 'SERVER_VERIFY_ERROR', error: errorMessage })
				}
			}
			// Mark data as processed (or null if no data)
			processedVerifyDataRef.current = verifyFetcher.data ?? null
		}
	}, [verifyFetcher.state, verifyFetcher.data, state.value, send])

	// Effect to reset verification flag if state changes away from verifying
	useEffect(() => {
		if (
			!state.matches('verifying_initial') &&
			!state.matches('subscribed_verifying')
		) {
			if (verificationFetchInFlightRef.current) {
				pushLogger.info(
					`Effect[state=${String(state.value)}]: State changed away from verifying, resetting fetch flag.`,
					{ state: state.value },
				)
				verificationFetchInFlightRef.current = false
			}
		}
	}, [state.value])

	// Removed Unregister Fetcher Result processing logic

	// --- Event Listeners / Triggers ---

	// Effect for Initialization and Logout
	useEffect(() => {
		// Send PROVIDER_INIT only once when the initial check completes and user is logged in
		if (
			initialCheckComplete &&
			!isInitializedRef.current &&
			userId // Ensure user is logged in for first init
		) {
			send({
				type: 'PROVIDER_INIT',
				payload: {
					userId,
					isServiceWorkerAvailable,
					canSendPush,
					initialPushSubscription: browserPushSubscription,
					initialCheckComplete,
					vapidPublicKey,
				},
			})
			isInitializedRef.current = true
		}

		// Handle user logout
		if (!userId && isInitializedRef.current) {
			// Only trigger logout logic if initialized
			pushLogger.info('User logged out, sending USER_LOGGED_OUT')
			send({ type: 'USER_LOGGED_OUT' })
			// Reset initialized flag so PROVIDER_INIT can run again if user logs back in
			isInitializedRef.current = false
			// Also reset processed data refs on logout
			processedRegisterDataRef.current = null
			processedVerifyDataRef.current = null
			// Removed processedUnregisterDataRef reset
		}
	}, [
		userId,
		vapidPublicKey,
		isServiceWorkerAvailable,
		canSendPush,
		browserPushSubscription,
		initialCheckComplete,
		send,
	])

	// Effect for Visibility/Focus Change
	const handleVisibilityChange = useCallback(() => {
		if (document.visibilityState === 'visible') {
			pushLogger.info('Visibility change: Sending VISIBILITY_CHECK')
			send({ type: 'VISIBILITY_CHECK' })
		}
	}, [send])

	useEffect(() => {
		document.addEventListener('visibilitychange', handleVisibilityChange)
		window.addEventListener('focus', handleVisibilityChange)
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('focus', handleVisibilityChange)
		}
	}, [handleVisibilityChange])

	// --- Actions Exposed via Context ---

	const requestPermission = useCallback(async () => {
		// Don't need to send PERMISSION_REQUESTED, browserRequestPermission handles UI
		const permission = await browserRequestPermission()
		if (permission === 'granted') {
			send({ type: 'PERMISSION_GRANTED' })
		} else {
			send({ type: 'PERMISSION_DENIED' })
		}
		return permission
	}, [browserRequestPermission, send])

	const testPushNotifications = useCallback(() => {
		pushLogger.info('Context Action: Sending TEST_PUSH_REQUESTED')
		send({ type: 'TEST_PUSH_REQUESTED' })
	}, [send])

	const setIsUpdatingSW = useCallback(
		(isUpdating: boolean) => {
			pushLogger.info(
				`Context Action: Sending ${isUpdating ? 'START_SW_UPDATE' : 'END_SW_UPDATE'}`,
				{ isUpdating },
			)
			send({ type: isUpdating ? 'START_SW_UPDATE' : 'END_SW_UPDATE' })
		},
		[send],
	)

	// Effect to reset test notification state
	useEffect(() => {
		if (testSent) {
			const timer = setTimeout(() => setTestSent(false), 5000)
			return () => clearTimeout(timer)
		}
	}, [testSent])

	// --- Context Value ---
	const contextValue: PushNotificationContextType = useMemo(
		() => ({
			isSubscribed: state.matches('subscribed_idle'),
			canSendPush,
			pushSubscription: state.context.pushSubscription,
			notificationPermission,
			requestPermission,
			testPushNotifications,
			isLoading:
				!state.matches('idle') &&
				!state.matches('subscribed_idle') &&
				!state.matches('error') &&
				!state.matches('rate_limited') &&
				!state.matches('initializing'), // Consider initializing as loading too
			testSent,
			initialCheckComplete,
			isUpdatingSW: state.matches('sw_updating'),
			setIsUpdatingSW,
			stateValue: state.value, // Expose the raw state string/object
			unsubscribeFromPush,
		}),
		[
			state.value, // state string value
			state.context.pushSubscription, // specific context value
			canSendPush,
			notificationPermission,
			requestPermission,
			testPushNotifications,
			testSent,
			initialCheckComplete,
			setIsUpdatingSW,
			unsubscribeFromPush,
		],
	)

	return (
		<PushNotificationContext.Provider value={contextValue}>
			{children}
		</PushNotificationContext.Provider>
	)
}

// --- Consumer Hook (Unchanged) ---
/**
 * usePushNotifications provides access to push notification state and actions.
 * Must be used within a PushProvider.
 */
export function usePushNotifications() {
	const context = useContext(PushNotificationContext)
	if (!context) {
		throw new Error(
			'usePushNotifications must be used within a PushNotificationProvider',
		)
	}
	return context
}

// --- Helper Types/Interfaces (If any specific ones were defined outside the machine) ---
// e.g., type PushSubscription = { endpoint: string; keys: { p256dh: string; auth: string } };
// Ensure these match the actual structure used.
